import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FolderOrganizer = ({ folders, onFolderAction, selectedFolder, onFolderSelect }) => {
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(['root']);

  const handleCreateFolder = () => {
    if (newFolderName?.trim()) {
      onFolderAction('create', { name: newFolderName, parent: selectedFolder });
      setNewFolderName('');
      setIsCreatingFolder(false);
    }
  };

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => 
      prev?.includes(folderId) 
        ? prev?.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const getFolderIcon = (folder) => {
    switch (folder?.type) {
      case 'contracts': return 'FileText';
      case 'amendments': return 'Edit3';
      case 'exhibits': return 'Paperclip';
      case 'correspondence': return 'Mail';
      default: return 'Folder';
    }
  };

  const renderFolderTree = (items, level = 0) => {
    return items?.map(folder => (
      <div key={folder?.id} className={`${level > 0 ? 'ml-4' : ''}`}>
        <div
          className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-legal-fast ${
            selectedFolder === folder?.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
          }`}
          onClick={() => onFolderSelect(folder?.id)}
        >
          <div className="flex items-center space-x-2 flex-1">
            <button
              onClick={(e) => {
                e?.stopPropagation();
                toggleFolder(folder?.id);
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <Icon 
                name={expandedFolders?.includes(folder?.id) ? 'ChevronDown' : 'ChevronRight'} 
                size={14} 
              />
            </button>
            <Icon 
              name={getFolderIcon(folder)} 
              size={16} 
              className={selectedFolder === folder?.id ? 'text-primary' : 'text-accent'} 
            />
            <span className="text-sm font-medium">{folder?.name}</span>
            <span className="text-xs text-muted-foreground">({folder?.count})</span>
          </div>
          
          <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1">
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e?.stopPropagation();
                onFolderAction('rename', folder);
              }}
              title="Rename folder"
            >
              <Icon name="Edit2" size={12} />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e?.stopPropagation();
                onFolderAction('delete', folder);
              }}
              title="Delete folder"
            >
              <Icon name="Trash2" size={12} />
            </Button>
          </div>
        </div>
        
        {expandedFolders?.includes(folder?.id) && folder?.children && (
          <div className="mt-1">
            {renderFolderTree(folder?.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Document Folders</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCreatingFolder(true)}
          iconName="FolderPlus"
          iconPosition="left"
        >
          New Folder
        </Button>
      </div>
      {/* Create New Folder */}
      {isCreatingFolder && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <Input
              type="text"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e?.target?.value)}
              className="flex-1"
              onKeyPress={(e) => e?.key === 'Enter' && handleCreateFolder()}
            />
            <Button
              variant="default"
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName?.trim()}
            >
              <Icon name="Check" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsCreatingFolder(false);
                setNewFolderName('');
              }}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}
      {/* Folder Tree */}
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {renderFolderTree(folders)}
      </div>
      {/* Folder Stats */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary">{folders?.length}</p>
            <p className="text-xs text-muted-foreground">Total Folders</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-success">
              {folders?.reduce((sum, folder) => sum + folder?.count, 0)}
            </p>
            <p className="text-xs text-muted-foreground">Total Documents</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FolderOrganizer;